import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "label", "row"]
  static values = {
    alreadyOrderedLabel: String,
    moveAfterTemplate: String,
    moveBeforeTemplate: String,
    ordered: Array,
    previewUnavailableLabel: String,
    resourceId: String,
    timeZones: Object
  }

  connect() {
    this.userUnchecked = false
    this.timelineEdited = false
    this.initializing = true
    this.refresh()
    this.initializationFrame = requestAnimationFrame(() => {
      this.initializing = false
    })
  }

  disconnect() {
    cancelAnimationFrame(this.initializationFrame)
  }

  selectionChanged() {
    this.userUnchecked = !this.checkboxTarget.checked
  }

  timelineChanged() {
    if (!this.initializing) this.timelineEdited = true
    this.refresh()
  }

  refresh() {
    const effectiveStartUtc = this.effectiveStartUtc()
    const proposal = this.proposalFor(effectiveStartUtc)

    if (!proposal) {
      this.hide()
      return
    }

    this.rowTarget.classList.remove("hidden")
    this.checkboxTarget.disabled = false
    if (this.timelineEdited && !this.userUnchecked) this.checkboxTarget.checked = true
    this.labelTarget.textContent = proposal.before
      ? this.moveBeforeTemplateValue.replace("__LABEL__", proposal.before.label)
      : this.moveAfterTemplateValue.replace("__LABEL__", proposal.after.label)
  }

  effectiveStartUtc() {
    const timeline = this.element.querySelector('[data-controller~="timeline"]')
    if (!timeline) return null

    const destroyField = timeline.querySelector('[name$="[_destroy]"]')
    if (destroyField?.value === "1") return null

    const offsetField = timeline.querySelector('[data-timeline-target~="offset"]')
    const offsetMilliseconds = this.durationToSeconds(offsetField?.value || "PT0S") * 1000
    const parentReferenceMilliseconds = Date.parse(timeline.dataset.timelineParentReferenceUtcValue || "")
    if (!Number.isNaN(parentReferenceMilliseconds)) {
      return new Date(parentReferenceMilliseconds + offsetMilliseconds).toISOString()
    }

    const anchor = timeline.querySelector('[data-timeline-target~="anchor"]')?.value
    const timeZoneName = timeline.querySelector('[data-timeline-target~="timeZoneField"]')?.value
    const timeZone = this.timeZonesValue[timeZoneName] || timeZoneName
    const anchorMilliseconds = this.localDateTimeToUtc(anchor, timeZone)
    if (anchorMilliseconds === null) return null

    return new Date(anchorMilliseconds + offsetMilliseconds).toISOString()
  }

  proposalFor(effectiveStartUtc) {
    const resourceStart = Date.parse(effectiveStartUtc || "")
    if (Number.isNaN(resourceStart)) return null

    const ordered = this.orderedValue.map((item) => ({
      ...item,
      id: item.id.toString(),
      start: item.id.toString() === this.resourceIdValue
        ? resourceStart
        : this.parseStart(item.effective_start)
    }))
    const currentIndex = ordered.findIndex((item) => item.id === this.resourceIdValue)
    if (currentIndex < 0) return null

    const timedItemOutOfOrder = ordered.some((item, index) => {
      if (item.id === this.resourceIdValue || item.start === null || item.start === resourceStart) return false

      return (index < currentIndex && item.start > resourceStart) ||
        (index > currentIndex && item.start < resourceStart)
    })
    if (!timedItemOutOfOrder) return null

    const withoutResource = ordered.filter((item) => item.id !== this.resourceIdValue)
    let targetIndex = withoutResource.findIndex((item) => item.start !== null && item.start > resourceStart)
    if (targetIndex < 0) targetIndex = withoutResource.length

    const equalItemsBefore = ordered.slice(0, currentIndex)
      .filter((item) => item.start === resourceStart).length
    const firstEqualIndex = withoutResource.findIndex((item) => item.start === resourceStart)
    if (firstEqualIndex >= 0) targetIndex = firstEqualIndex + equalItemsBefore

    if (targetIndex === currentIndex) return null

    const proposed = [...withoutResource]
    proposed.splice(targetIndex, 0, ordered[currentIndex])
    return {
      before: proposed[targetIndex + 1] || null,
      after: targetIndex > 0 ? proposed[targetIndex - 1] : null
    }
  }

  parseStart(value) {
    if (!value) return null

    const milliseconds = Date.parse(value)
    return Number.isNaN(milliseconds) ? null : milliseconds
  }

  durationToSeconds(value) {
    let normalized = value.toString().trim()
    let sign = 1
    if (normalized.startsWith("-")) {
      sign = -1
      normalized = normalized.slice(1)
    }

    const match = normalized.match(/^P(?:(-?\d+(?:\.\d+)?)D)?(?:T(?:(-?\d+(?:\.\d+)?)H)?(?:(-?\d+(?:\.\d+)?)M)?(?:(-?\d+(?:\.\d+)?)S)?)?$/)
    if (!match) return 0

    const [, days = 0, hours = 0, minutes = 0, seconds = 0] = match
    const total = (Number(days) * 86400) + (Number(hours) * 3600) + (Number(minutes) * 60) + Number(seconds)
    return sign * total
  }

  localDateTimeToUtc(value, timeZone) {
    const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?(?: UTC|Z)?$/)
    if (!match || !timeZone) return null

    const [, year, month, day, hour = 0, minute = 0, second = 0] = match
    const desiredMilliseconds = Date.UTC(year, Number(month) - 1, day, hour, minute, second)
    let utcMilliseconds = desiredMilliseconds

    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
        timeZone
      })

      for (let attempt = 0; attempt < 3; attempt += 1) {
        const parts = Object.fromEntries(
          formatter.formatToParts(new Date(utcMilliseconds))
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)])
        )
        const representedMilliseconds = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        )
        const correction = desiredMilliseconds - representedMilliseconds
        utcMilliseconds += correction
        if (correction === 0) break
      }
    } catch (_error) {
      return null
    }

    return utcMilliseconds
  }

  hide() {
    this.checkboxTarget.checked = false
    this.checkboxTarget.disabled = true
    this.rowTarget.classList.add("hidden")
  }
}
